// gap-check.ts
import { ChangeDetectorRef, Component, ChangeDetectionStrategy, OnInit, inject } from '@angular/core';
import * as htmlToImage from 'html-to-image';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import {
  Chart,
  RadarController,
  BarController,
  RadialLinearScale,
  BarElement,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  Title,
  ChartOptions,
  CategoryScale,
  LinearScale,
} from 'chart.js';
import { RouterLink, Router, ActivatedRoute } from '@angular/router';
import jsPDF from 'jspdf';
import { StepperComponent } from './components/stepper/stepper';
import { QuestionsComponent } from './components/questions/questions';
import { SummaryComponent } from './components/summary/summary';
import { MailService } from '../../services';
import axios from 'axios';
import { AlertModal, AlertType } from '../../components/layouts/alert-modal/alert-modal';
import { CustomerService } from '../../services/customer-service/customer-service';
import { EmailPayloadModel } from '../../shared';
import { getEnvName } from '../../shared/helpers';
import { SummaryReport } from './components/summary-report/summary-report';
import { Rating, RatingService } from '../../services/rating-service/rating-service';
import { Customer } from '../../shared/models/customer.model';
import { GapCheckSection } from '../../shared/models/gap-check-section';
import { ISO_27001_QUESTIONS, ISO_42001_QUESTIONS } from '../gap-check/gap-check-questions';

// Register chart components
Chart.register(
  RadarController,
  BarController,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  Title,
  CategoryScale,
  LinearScale,
  BarElement,
);

@Component({
  selector: 'app-gap-check',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    RouterLink,
    StepperComponent,
    QuestionsComponent,
    SummaryComponent,
    AlertModal,
    SummaryReport,
  ],
  templateUrl: './gap-check.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./gap-check.css'],
})
export class GapCheck implements OnInit {
  private mailService = inject(MailService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);
  private customerService = inject(CustomerService);
  private ratingService = inject(RatingService);

  customer?: Customer;
  selectedStandard: string | null = null;

  alertKey = 0;
  showAlert = false;
  alertMessage = "";
  alertTitle = "";
  isLoading = false;
  reportFile: File | null = null;
  currentStep = 0;
  formSubmitted = false;
  alertType: AlertType = "information";

  totalGraphicScore = 0;
  rating: Rating | null = null;

  assessmentDate: string = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  envName: string = getEnvName();

  formData: EmailPayloadModel = {
    subject: "Your ISO/IEC Self-Assessment Results from AuditFixers.com",
    name: "",
    email: "",
    contactNumber: "",
    message: "",
    companyName: "",
    score: 0,
    rating: "Likely Compliant",
    summary: "Test summary",
  };

  questions: GapCheckSection[] = [];

  // Radar chart labels computed from questions
  radarChartLabels: string[] = [];

  get radarChartData() {
    const labels = this.questions.filter((q) => q.title !== 'Summary').map((q) => q.title);
    const currentData = this.questions
      .filter((q) => q.title !== 'Summary')
      .map((_, i) => this.getStepScore(i).graphic);

    return {
      labels,
      datasets: [
        {
          label: 'Current Score',
          data: currentData,
          fill: true,
          backgroundColor: 'rgba(4, 74, 51, 0.2)',
          borderColor: 'rgba(16, 185, 129, 1)',
          pointBackgroundColor: 'rgba(16, 185, 129, 1)',
          pointBorderColor: '#fff',
        },
      ],
    };
  }

  radarChartOptions: ChartOptions = {
    responsive: true,
    plugins: {
      legend: { display: true, position: 'top' },
    },
    scales: {
      r: {
        suggestedMin: 0,
        suggestedMax: 10,
      },
    },
  };

  // Bar chart data – explicit type for data array
  barChartData = {
    labels: this.radarChartLabels,
    datasets: [
      {
        label: 'Current Score',
        data: [] as number[],
        backgroundColor: 'rgba(16, 185, 129, 0.7)',
      },
    ],
  };

  barChartOptions: ChartOptions = {
    responsive: true,
    plugins: {
      legend: { display: true, position: 'top' },
    },
    scales: {
      y: {
        suggestedMin: 0,
        suggestedMax: 10,
      },
    },
  };

  ngOnInit() {
    // Read standard from query parameter
    this.route.queryParams.subscribe(params => {
      const standard = params['standard'];
      let loadedQuestions: GapCheckSection[];

      if (standard === '27001') {
        loadedQuestions = ISO_27001_QUESTIONS;
        this.selectedStandard = '27001';
      } else if (standard === '42001') {
        loadedQuestions = ISO_42001_QUESTIONS;
        this.selectedStandard = '42001';
      } else {
        // No valid standard – redirect to intro
        this.router.navigate(['/gap-check/intro']);
        return;
      }

      // Deep copy to avoid mutating the original arrays
      this.questions = loadedQuestions.map(section => ({
        ...section,
        questions: section.questions.map(q => ({ ...q, value: '' })),
      }));

      // Reset state – always start at step 0
      this.currentStep = 0;
      this.formSubmitted = false;
      this.totalGraphicScore = 0;

      // Update chart labels and data
      this.radarChartLabels = this.questions.filter(q => q.title !== 'Summary').map(q => q.title);
      this.barChartData.labels = this.radarChartLabels;
      this.updateBarChart();

      // ✅ REMOVED dev pre‑fill – users always start at the first question

      this.rating = this.ratingService.getRating(this.totalGraphicScore);
      this.cdr.detectChanges();
    });

    // Load customer data
    const customer = this.customerService.getCustomer();
    if (customer) {
      this.formData.email = customer.email;
      this.formData.name = customer.name;
      this.formData.companyName = customer.companyName;
      this.formData.contactNumber = customer.contactNumber;
    }
  }

  // -------------------------
  // STEP NAVIGATION
  // -------------------------
  nextStep() {
    this.updateBarChart();
    if (this.currentStep < this.questions.length - 1 && this.isCurrentStepComplete()) {
      this.currentStep++;
    }
  }

  prevStep() {
    this.updateBarChart();
    if (this.currentStep > 0) this.currentStep--;
  }

  isCurrentStepComplete(): boolean {
    this.updateBarChart();
    const stepQuestions = this.questions[this.currentStep].questions;
    if (!stepQuestions || stepQuestions.length === 0) return true;
    return stepQuestions.every((q) => q.value !== '');
  }

  goToStep(i: number) {
    this.updateBarChart();
    if (i <= this.currentStep) {
      this.currentStep = i;
    } else if (this.isCurrentStepComplete()) {
      this.currentStep = i;
    }
  }

  // -------------------------
  // SCORING
  // -------------------------
  getStepScore(stepIndex: number): {
    yes: number;
    no: number;
    partial: number;
    na: number;
    total: number;
    score: number;
    percentage: number;
    graphic: number;
  } {
    const step = this.questions.at(stepIndex);
    if (!step?.questions?.length) {
      return {
        yes: 0,
        no: 0,
        partial: 0,
        na: 0,
        total: 0,
        score: 0,
        percentage: 0,
        graphic: 0,
      };
    }

    let yes = 0;
    let no = 0;
    let partial = 0;
    let na = 0;

    step.questions.forEach((q) => {
      switch (q.value) {
        case 'Yes':
          yes++;
          break;
        case 'Partial':
          partial++;
          break;
        case 'No':
          no++;
          break;
        case 'N/A':
          na++;
          break;
      }
    });

    const total = yes + no + partial;
    const score = yes + partial * 0.5;
    const percentage = total > 0 ? (score / total) * 100 : 0;
    let graphic = total > 0 ? (score / total) * 10 : 0;
    graphic = Math.round(graphic * 10) / 10;
    graphic = parseFloat(graphic.toFixed(1));

    return { yes, no, partial, na, total, score, percentage, graphic };
  }

  getOverallScore(): { score: number; total: number; percentage: number } {
    let totalScore = 0;
    let totalQuestions = 0;
    this.questions.forEach((step, index) => {
      const { score, total } = this.getStepScore(index);
      totalScore += score;
      totalQuestions += total;
    });
    const percentage = totalQuestions > 0 ? (totalScore / totalQuestions) * 100 : 0;
    return { score: totalScore, total: totalQuestions, percentage };
  }

  getAllScoresTotal() {
    const totals = {
      yes: 0,
      no: 0,
      partial: 0,
      na: 0,
      total: 0,
      graphic: 0,
      percentage: 0,
    };

    const sections = this.questions.filter((s) => s.questions.length > 0 && s.title !== 'Summary');
    let totalQuestions = 0;
    let weightedScore = 0;

    sections.forEach((section, i) => {
      const score = this.getStepScore(i);
      totals.yes += score.yes;
      totals.no += score.no;
      totals.partial += score.partial;
      totals.na += score.na;
      totals.total += score.total;
      weightedScore += score.yes * 1 + score.partial * 0.5;
      totalQuestions += score.total;
    });

    totals.percentage = totalQuestions > 0 ? parseFloat((weightedScore / totalQuestions).toFixed(2)) : 0;
    totals.graphic = totalQuestions > 0 ? parseFloat(((weightedScore / totalQuestions) * 10).toFixed(1)) : 0;

    ['yes', 'no', 'partial', 'na', 'total'].forEach((key) => {
      totals[key as keyof typeof totals] = parseFloat(totals[key as keyof typeof totals].toFixed(1));
    });

    return totals;
  }

  // -------------------------
  // ALERT MODAL
  // -------------------------
  showAlertModal(title: string, message: string, loading = false, type: AlertType = "information") {
    this.alertTitle = title;
    this.alertMessage = message;
    this.isLoading = loading;
    this.showAlert = true;
    this.alertType = type;
    this.cdr.detectChanges();
  }

  closeAlertModal() {
    this.showAlert = false;
    this.isLoading = false;
    this.alertTitle = "";
    this.alertMessage = "";
    this.alertType = "information";
    this.cdr.detectChanges();
  }

  delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // -------------------------
  // SUBMIT
  // -------------------------
  async submitForm() {
    console.log('=== Gap Check Answers ===');
    const overall = this.getOverallScore();
    const allTotals = this.getAllScoresTotal();
    
    this.totalGraphicScore = allTotals.graphic;
    this.rating = this.ratingService.getRating(this.totalGraphicScore);
    this.questions = [...this.questions];
    this.cdr.detectChanges(); // force view update so the summary report catches changes

    console.log(`Overall Score: ${overall.score}/${overall.total} (${overall.percentage.toFixed(1)}%)`);

    this.isLoading = true;
    this.showAlertModal("Information", "Generating assessment result, please wait...", true, "information");
    await new Promise((resolve) => setTimeout(resolve, 500));

    try {
      const file = await this.generatePdf();
      console.log('file >> ', file);
      this.reportFile = file;
      if (file) {
        this.showAlertModal("Information", "Please wait while we are submitting your assessment result.", true, "information");
        this.sendReport(file);
      } else {
        this.showAlertModal('Error', 'Failed to generate the report.', false, "error");
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      this.showAlertModal('Error', 'Failed to generate the report.', false, "error");
    }
  }

  updateBarChart() {
    this.barChartData.datasets[0].data = this.questions
      .filter((q) => q.title !== 'Summary')
      .map((q, i) => this.getStepScore(i).graphic);
  }

  async downloadReport(file?: File) {
    const pdfFile: File | null = this.reportFile ?? file ?? null;
    if (!pdfFile) {
      console.error('No report file available to download.');
      return;
    }
    const url = window.URL.createObjectURL(pdfFile);
    const a = document.createElement('a');
    a.href = url;
    a.download = pdfFile.name || 'report.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    this.router.navigate(['/']);
  }

  sendReport(attachment?: File): void {
    this.isLoading = true;
    this.cdr.detectChanges();

    const ratingElement = document.getElementById('rating');
    const rating = ratingElement ? ratingElement.innerText.trim() : 'N/A';

    const payload = {
      subject: this.formData.subject,
      name: this.formData.name,
      email: this.formData.email,
      mobile: this.formData.contactNumber,
      message: this.formData.message,
      score: this.totalGraphicScore,
      rating: rating,
      summary: this.rating?.description || "",
      company_name: this.formData.companyName,
      standard: this.selectedStandard,
    };

    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => formData.append(key, value as string));
    if (attachment) {
      formData.append('attachment', attachment, attachment.name);
    }

    this.mailService.sendMail(formData)
      .then(response => {
        console.log('Email sent successfully:', response.data);
        this.isLoading = false;
        this.formSubmitted = true;
        this.closeAlertModal();
      })
      .catch(error => {
        this.isLoading = false;
        this.cdr.detectChanges();
        setTimeout(() => {
          this.showAlertModal("Error", "Unable to process your request at the moment. Please try again later.", false, "error");
        }, 300);
        if (axios.isAxiosError(error)) {
          console.error('Axios error sending email:', error.response?.data);
          console.error('Status Code:', error.response?.status);
        } else {
          console.error('Unexpected error sending email:', error);
        }
      });
  }

  async generatePdf(): Promise<File> {
    const summary = document.querySelector('.summary-report') as HTMLElement;
    if (!summary) throw new Error('Summary report element not found');

    const clone = summary.cloneNode(true) as HTMLElement;
    clone.classList.remove('hidden');
    clone.style.display = 'block';
    clone.style.visibility = 'visible';
    clone.style.opacity = '1';

    // compress: true enables Flate compression of the PDF's internal streams.
    // JPEG images are already compressed, but this still helps the small
    // amount of non-image content (fonts/structure).
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'letter',
      compress: true,
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const contentWidth = pdfWidth - margin * 2;

    clone.style.width = `${pdfWidth * 3.78}px`;
    clone.style.margin = '0';
    clone.style.background = '#fff';
    clone.style.textAlign = 'left';
    clone.style.position = 'fixed';
    clone.style.left = '-10000px';
    clone.style.top = '0';
    clone.style.pointerEvents = 'none';

    clone.querySelectorAll<HTMLElement>('*').forEach(el => {
      el.style.marginLeft = '0';
      el.style.transform = 'none';
    });

    const wrapper = document.createElement('div');
    wrapper.style.position = 'fixed';
    wrapper.style.top = '0';
    wrapper.style.left = '0';
    wrapper.style.width = '0';
    wrapper.style.height = '0';
    wrapper.style.overflow = 'hidden';
    wrapper.style.opacity = '0';
    wrapper.style.pointerEvents = 'none';
    wrapper.style.zIndex = '-1';

    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);

    await new Promise(res => setTimeout(res, 300));

    const pages = Array.from(clone.querySelectorAll('.page')) as HTMLElement[];
    if (!pages.length) throw new Error('No pages found in summary report');

    for (let i = 0; i < pages.length; i++) {
      const page = pages.at(i);
      if (!page) continue;

      // Measure where it is SAFE to cut this page, in CSS px from its top,
      // before rasterising. A cut is only allowed at the bottom edge of a
      // question row or a non-table block (heading, paragraph, footer) -
      // never straight after the column header row or a "Clause N" row,
      // which would strand that header alone at the foot of a page.
      // Rendered page heights vary by OS - the same ISO 42001 report
      // measured 2123px here and 2305px on a tester's Windows machine,
      // because Tailwind's `font-sans` resolves to each OS's system font and
      // those wrap differently - so the cut points cannot be precomputed;
      // they have to come from the actual layout being drawn.
      const pageTop = page.getBoundingClientRect().top;
      const cutPointsCss = Array.from(
        page.querySelectorAll<HTMLElement>('tbody > tr:not(:has(> td[colspan])), :scope > :not(table)')
      )
        .map(el => el.getBoundingClientRect().bottom - pageTop)
        .filter(y => y > 0)
        .sort((a, b) => a - b);

      // JPEG instead of PNG: each page is a raster screenshot of mostly
      // white background, text and table borders, which PNG (lossless)
      // compresses poorly with anti-aliased edges. A report was measured at
      // ~21MB as PNG - large enough to make the upload itself feel like a
      // hang, and past Microsoft Graph's ~4MB inline sendMail attachment
      // limit, so large reports likely failed delivery silently. JPEG at
      // quality 0.85 is visually indistinguishable for this content and
      // brings a typical report to well under 1MB.
      const dataUrl = await htmlToImage.toJpeg(page, {
        cacheBust: true,
        backgroundColor: '#ffffff',
        pixelRatio: 1.6,
        quality: 0.85,
        width: page.scrollWidth,
        height: page.scrollHeight,
      });

      const imgProps = pdf.getImageProperties(dataUrl);
      const imgHeight = (imgProps.height * contentWidth) / imgProps.width;

      const printableHeight = pdfHeight - margin * 2;
      const scale = contentWidth / imgProps.width;
      // Tallest slice that still fits inside the printable area, in image px.
      const maxSliceHeight = Math.floor(printableHeight / scale);

      if (imgHeight <= printableHeight) {
        if (i > 0) pdf.addPage();
        pdf.addImage(dataUrl, 'JPEG', margin, margin, contentWidth, imgHeight);
        continue;
      }

      // Slightly too tall (up to 10% over): shrink the whole page to fit
      // rather than split it. Splitting here produced a near-empty page - a
      // tester's report overflowed by 47px and got a page holding half the
      // contact footer and nothing else. A 3-10% reduction is far less
      // noticeable than that.
      if (imgProps.height <= maxSliceHeight * 1.10) {
        if (i > 0) pdf.addPage();
        const fitScale = printableHeight / imgHeight;
        const fitWidth = contentWidth * fitScale;
        pdf.addImage(dataUrl, 'JPEG', margin + (contentWidth - fitWidth) / 2, margin, fitWidth, printableHeight);
        continue;
      }

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      canvas.width = imgProps.width;

      const img = new Image();
      img.src = dataUrl;
      await new Promise<void>(resolve => { img.onload = () => resolve(); });

      // Convert the CSS-px cut points measured above into image px. Derive
      // the ratio from the actual raster rather than assuming pixelRatio, so
      // any rounding html-to-image applies is accounted for.
      const pxPerCss = imgProps.height / page.scrollHeight;
      const cutPoints = cutPointsCss.map(y => Math.round(y * pxPerCss));

      // Lowest safe cut at or below `limit` px from `from`, or 0 if none.
      const safeCutBelow = (from: number, limit: number): number => {
        const safe = cutPoints.filter(y => y > from && y - from <= limit);
        return safe.length ? safe[safe.length - 1] - from : 0;
      };

      let offset = 0;
      let firstSlice = i === 0;

      while (offset < imgProps.height) {
        const remaining = imgProps.height - offset;
        let sliceHeight = Math.min(maxSliceHeight, remaining);

        if (remaining > maxSliceHeight) {
          // Prefer the lowest safe cut point that still fits on this page,
          // so the break never lands in the middle of a table row. Fall back
          // to a hard cut only if a single block is taller than a whole page.
          const greedy = safeCutBelow(offset, maxSliceHeight);
          if (greedy) sliceHeight = greedy;

          // If that would leave a stub of under a fifth of a page (content
          // just past the shrink threshold does this: 12% over -> one full
          // page plus a page holding two rows), split closer to the middle
          // instead so the two pages look deliberate rather than accidental.
          const leftover = remaining - sliceHeight;
          if (leftover > 0 && leftover <= maxSliceHeight && leftover < maxSliceHeight * 0.2) {
            const balanced = safeCutBelow(offset, Math.floor(remaining / 2) + Math.floor(maxSliceHeight * 0.1));
            if (balanced) sliceHeight = balanced;
          }
        }

        // The canvas has to be resized to the slice, not left at
        // maxSliceHeight. toDataURL() always exports the WHOLE canvas, so a
        // final short slice on a full-height canvas produced a JPEG padded
        // with blank space below the content - which addImage() then
        // squashed into sliceHeight, because that is the height it was
        // told the image was. That is the "distorted last page" bug.
        // Assigning .height also clears the bitmap, so re-fill white after.
        canvas.height = sliceHeight;
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, offset, imgProps.width, sliceHeight, 0, 0, canvas.width, sliceHeight);

        const pageDataUrl = canvas.toDataURL('image/jpeg', 0.85);
        if (!firstSlice) pdf.addPage();
        pdf.addImage(pageDataUrl, 'JPEG', margin, margin, contentWidth, sliceHeight * scale);

        firstSlice = false;
        offset += sliceHeight;
      }
    }

    document.body.removeChild(wrapper);

    const blob = pdf.output('blob');
    return new File(
      [blob],
      'ISO_Gap_Assessment_Report.pdf',
      { type: 'application/pdf' }
    );
  }
}